/**
 * Asset URL Helper
 *
 * This utility helps construct proper URLs for static assets (uploads, lessons, etc.)
 * It handles both development and production environments correctly:
 *
 * - Development: Uses relative URLs to leverage the webpack dev server proxy
 * - Production: Uses the REACT_APP_ASSET_URL environment variable
 */

/**
 * Get the base URL for assets
 *
 * @returns Base URL for assets (uploads directory)
 */
export const getAssetBaseUrl = (): string => {
  // In development, use relative URLs to leverage the proxy
  // This avoids cross-origin issues with SCORM content
  if (process.env.NODE_ENV === 'development') {
    return '/uploads';
  }

  // In production, use the environment variable
  // This should point to the same domain or a properly configured CDN
  return process.env.REACT_APP_ASSET_URL || '/uploads';
};

/**
 * Get the base URL for course lesson content
 *
 * @returns Base URL for lesson content
 */
export const getLessonContentBaseUrl = (): string => {
  return `${getAssetBaseUrl()}/courses/lessons`;
};

/**
 * Construct a full URL for a lesson content file
 *
 * @param contentType - Type of content (document, mp4, scorm, url)
 * @param fileName - Name of the file
 * @returns Full URL to the content file
 */
export const getLessonContentUrl = (
  contentType: 'document' | 'mp4' | 'scorm' | 'url',
  fileName?: string
): string => {
  if (!fileName) return '';

  const baseUrl = getLessonContentBaseUrl();

  switch (contentType) {
    case 'document':
      return `${baseUrl}/documents/${fileName}`;
    case 'mp4':
      return `${baseUrl}/mp4/${fileName}`;
    case 'scorm':
      return `${baseUrl}/scorm_packages/${fileName}/story.html`;
    case 'url':
      return fileName; // URLs are returned as-is
    default:
      return '';
  }
};
